import type { ProposedTodo } from "../hooks/useVoiceToTodo";
import type { NewSubtaskFields, NewTask, SubTask } from "../types";
import {
  proposedTodoToNewTask,
  proposedTodoToSubtaskFields,
} from "./proposedTodo";

/** Cards that stay new tasks. A card attached to an existing task does not count. */
export function newTaskCount(
  todos: Pick<ProposedTodo, "parent_task_id">[],
): number {
  return todos.filter((todo) => !todo.parent_task_id).length;
}

export function isOverTaskLimit(
  todos: Pick<ProposedTodo, "parent_task_id">[],
  maxTasks: number | undefined,
): boolean {
  return typeof maxTasks === "number" && newTaskCount(todos) > maxTasks;
}

/**
 * Saves each reviewed card. A card with a parent becomes a sub-task of that
 * task. Every other card becomes a new task. `null` from a writer means that
 * card failed and the caller should keep the capture.
 */
export async function saveReviewedTodos(
  todos: ProposedTodo[],
  deps: {
    addTask: (
      task: Omit<NewTask, "links" | "pending_files">,
    ) => Promise<unknown>;
    addSubtask: (
      taskId: string,
      title: string,
      fields?: NewSubtaskFields,
    ) => Promise<SubTask | null>;
    setSubtaskTags: (subtaskId: string, tagIds: string[]) => Promise<boolean>;
  },
): Promise<void> {
  for (const todo of todos) {
    if (todo.parent_task_id) {
      const title = todo.title.trim();
      if (!title) throw new Error("Failed to create subtask from review");
      const created = await deps.addSubtask(
        todo.parent_task_id,
        title,
        proposedTodoToSubtaskFields(todo),
      );
      if (!created) throw new Error("Failed to create subtask from review");
      if (todo.tag_ids.length > 0) {
        const tagged = await deps.setSubtaskTags(created.id, todo.tag_ids);
        if (!tagged) {
          // Load the client here. A top-level import stops the capture
          // checks in CI, which has no database address.
          const { supabase } = await import("./supabase");
          await supabase.from("subtasks").delete().eq("id", created.id);
          throw new Error("Failed to save tags on subtask");
        }
      }
      continue;
    }
    const created = await deps.addTask(proposedTodoToNewTask(todo));
    if (created === null) throw new Error("Failed to create task from review");
  }
}
